import { useMemo } from 'react';
import { useTimeFormat } from './useTimeFormat';
import {
  formatDateShort,
  formatDateTimeLong,
  formatDateTimeCompact,
  formatDateTimeWithSeconds,
  type DateInput,
} from '@/lib/format-date';

/**
 * #2067 — the date-time formatters, bound to the profile Time Format.
 *
 * The functions in `lib/format-date` take `hour12` as a trailing argument so
 * that code with no React context can still call them. Every component should
 * use this instead: it reads the profile once and binds it, so no call site
 * can pass the wrong value or forget to pass one.
 *
 * Reactivity is the reason this is a hook and not a module-level value. The
 * formatters are read during render, so a component only re-renders with a new
 * Time Format if it is subscribed to the store — which `useTimeFormat` does.
 * A module-level cache updated by an effect would leave every one of these
 * surfaces showing the old format until something unrelated re-rendered it.
 *
 * The identity of the returned object is stable while `hour12` is, so passing
 * it into a dependency array does not re-run the effect on every render.
 */
export function useDateFormatters() {
  const { hour12 } = useTimeFormat();

  return useMemo(
    () => ({
      /** "4/3/26, 10:33 AM" — grid display. */
      dateShort: (value: DateInput, fallback = '') => formatDateShort(value, fallback, hour12),
      /** "Apr 03, 2026 at 10:33 AM" */
      dateTimeLong: (value: DateInput, fallback = '') =>
        formatDateTimeLong(value, fallback, hour12),
      /** "04/03/2026, 10:33 AM" */
      dateTimeCompact: (value: DateInput, fallback = '') =>
        formatDateTimeCompact(value, fallback, hour12),
      /** "Apr 3, 2026, 10:33:09 AM" — pass `locale` for localized month names. */
      dateTimeWithSeconds: (value: DateInput, fallback = '', locale?: string) =>
        formatDateTimeWithSeconds(value, fallback, locale, hour12),
    }),
    [hour12],
  );
}
